import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import ResultPage from './components/ResultPage';
import SearchPage from './components/SearchPage';
import { normalizePlate } from './utils/plates';
import { fetchVehicle } from './utils/api';

const FAVOURITES_STORAGE_KEY = 'rekkari:favourites';

function getVehicleNotFoundMessage(plate) {
  return `No vehicle found for ${plate}.`;
}

function loadStoredFavourites() {
  const storedValue = window.localStorage.getItem(FAVOURITES_STORAGE_KEY);

  if (!storedValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.filter((vehicle) => vehicle && typeof vehicle.plate === 'string');
  } catch {
    return [];
  }
}

function RoutedApp({ favourites, onAddFavourite, onRemoveFavourite }) {
  const navigate = useNavigate();
  const { plate } = useParams();
  const [plateInput, setPlateInput] = useState('');
  const [error, setError] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const normalizedRoutePlate = plate ? normalizePlate(plate) : '';

  useEffect(() => {
    if (plate && plate !== normalizedRoutePlate) {
      navigate(`/${encodeURIComponent(normalizedRoutePlate)}`, { replace: true });
    }
  }, [plate, normalizedRoutePlate, navigate]);

  const loadVehicle = useCallback(async (plateToLoad) => {
    setIsLoading(true);
    setError('');
    try {
      const vehicle = await fetchVehicle(plateToLoad);
      setSelectedVehicle(vehicle);
    } catch (err) {
      setError(err.message);
      setSelectedVehicle(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (normalizedRoutePlate) {
      loadVehicle(normalizedRoutePlate);
    } else {
      setSelectedVehicle(null);
      setError('');
    }
  }, [normalizedRoutePlate, loadVehicle]);

  const handleSearch = (event) => {
    event.preventDefault();

    const normalizedPlate = normalizePlate(plateInput);

    if (!normalizedPlate) {
      setError('Enter a licence plate first.');
      return;
    }

    setError('');
    navigate(`/${encodeURIComponent(normalizedPlate)}`);
  };

  const handleReset = () => {
    setError('');
    setPlateInput('');
    setSelectedVehicle(null);
    navigate('/');
  };

  const handleOpenVehicle = (nextPlate) => {
    setError('');
    setPlateInput(nextPlate);
    navigate(`/${encodeURIComponent(nextPlate)}`);
  };

  const sortedFavourites = [...favourites].sort((a, b) => (b.powerKw || 0) - (a.powerKw || 0));

  if (selectedVehicle || isLoading) {
    return (
      <ResultPage
        favourites={sortedFavourites}
        isLoading={isLoading}
        onAddFavourite={() => onAddFavourite(selectedVehicle)}
        onOpenVehicle={handleOpenVehicle}
        onRemoveFavourite={onRemoveFavourite}
        onReset={handleReset}
        selectedVehicle={selectedVehicle}
      />
    );
  }

  return (
    <SearchPage
      error={error}
      favourites={sortedFavourites}
      onOpenVehicle={handleOpenVehicle}
      plateInput={plateInput}
      onPlateInputChange={setPlateInput}
      onRemoveFavourite={onRemoveFavourite}
      onSearch={handleSearch}
    />
  );
}

function App() {
  const [favourites, setFavourites] = useState(loadStoredFavourites);

  useEffect(() => {
    window.localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(favourites));
  }, [favourites]);

  const handleAddFavourite = (vehicle) => {
    setFavourites((currentFavourites) => {
      if (currentFavourites.some((currentVehicle) => currentVehicle.plate === vehicle.plate)) {
        return currentFavourites;
      }

      return [...currentFavourites, vehicle];
    });
  };

  const handleRemoveFavourite = (plate) => {
    setFavourites((currentFavourites) => currentFavourites.filter((vehicle) => vehicle.plate !== plate));
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <RoutedApp
              favourites={favourites}
              onAddFavourite={handleAddFavourite}
              onRemoveFavourite={handleRemoveFavourite}
            />
          }
        />
        <Route
          path="/:plate"
          element={
            <RoutedApp
              favourites={favourites}
              onAddFavourite={handleAddFavourite}
              onRemoveFavourite={handleRemoveFavourite}
            />
          }
        />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
